import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../../services/error-handling/error-handling.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SpinnerService } from '../../../services/spinner';
import { Observable, of } from 'rxjs';
import { HttpHeaderConstants } from '../../../services/http-header.constant';
import { catchError, finalize } from 'rxjs/operators';
import { AlertCriteriaSearch } from '../../../models/appcloud/alert/alert-criteria-search.model';
import { AlertCriteria } from '../../../models/appcloud/alert/alert-criteria.model';

@Injectable({
    providedIn: 'root',
})
export class AlertCriteriaService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private ALERT_CRITERIA_URL: string = environment.publicApiHost + '/appcloud/alert-criteria';

    search(search: AlertCriteriaSearch, additionalHeaders: any = {}): Observable<AlertCriteria[]> {
        this.#spinnerService.addProcess('AlertCriteriaService.search', 'Searching for you alert criteria...');

        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams(),
        };

        for (const [key, value] of Object.entries(search)) {
            httpOptions.params = httpOptions.params.set(key, value);
        }

        return this.#http.get<AlertCriteria[]>(this.ALERT_CRITERIA_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while searching for your alert criteria. Please try again later.'
                );
                return of<AlertCriteria[]>();
            }),
            finalize(() => {
                this.#spinnerService.completeProcess('AlertCriteriaService.search');
            })
        );
    }

    update(
        alertCriterionId: number,
        alertCriteria: AlertCriteria,
        additionalHeaders: any = {}
    ): Observable<AlertCriteria> {
        this.#spinnerService.addProcess('AlertCriteriaService.update', 'Updating your alert criterion...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<AlertCriteria>(`${this.ALERT_CRITERIA_URL}/${alertCriterionId}`, alertCriteria, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your alert criterion. Please try again later.'
                    );
                    return of<AlertCriteria>();
                }),
                finalize(() => this.#spinnerService.completeProcess('AlertCriteriaService.update'))
            );
    }
}
